/**
 * Confirmar antes de hacer algo, declarado en el HTML
 * -----------------------------------------------------------------------------
 * El `confirm()` del navegador saca una caja gris del sistema, con la marca del
 * sitio arriba y los botones del idioma del navegador. No se parece a nada de la
 * aplicación, no se puede escribir distinto, y dentro de un `iframe` sale
 * encabezada por «Una página insertada en … dice», que es lo que ve quien usa la
 * suite. **La regla de la casa es que todo aviso es un modal del theme**, y
 * había que acordarse de escribirlo a mano en cada pantalla.
 *
 * Aquí se declara y ya:
 *
 *   <form method="post" action="…"
 *         data-company-confirm="¿Quitar «guía.pdf»?"
 *         data-company-confirm-title="Quitar el papel"
 *         data-company-confirm-ok="Quitar"
 *         data-company-confirm-danger>
 *
 * Vale igual en un `<a>` o en un `<button>`:
 *
 *   <a href="…" data-company-confirm="¿Salir sin guardar?">Salir</a>
 *
 * ## Por qué está delegado en el documento
 *
 * Porque **funciona con lo que llega después**: una tabla remota que repinta sus
 * filas, un panel metido con `innerHTML`, un renglón recién añadido. Un
 * escuchador puesto elemento a elemento hay que volver a poner en cada repintado,
 * y el día que se olvide el formulario se envía sin preguntar - borrando algo -.
 * Eso no da ningún error: simplemente pasa.
 *
 * ## Y por qué no hace falta llamar a nada
 *
 * No hay `setupConfirmActions(root)`. Se registra una vez al cargar el módulo y
 * ya cubre la página entera, presente y futura. Una función de arranque sería
 * una más que recordar en cada `reinit()`.
 */

import { confirm } from './dialog.js';

const ATTR = 'data-company-confirm';

/** Puesto en el elemento mientras se deja pasar la acción ya confirmada. */
const YA = '__companyConfirmado';

function texto(origen, sufijo) {
  const valor = origen.getAttribute(`${ATTR}-${sufijo}`);

  return valor && valor.trim() !== '' ? valor : undefined;
}

function preguntar(origen) {
  return confirm({
    // `question` por defecto: preguntar no es avisar de un peligro. Que sea
    // peligroso lo declara quien llama con `-danger`, y eso pinta el botón rojo.
    type: texto(origen, 'type') || 'question',
    title: texto(origen, 'title') || origen.getAttribute(ATTR) || '',
    // Con título propio, el atributo principal pasa a ser el cuerpo; sin él ya
    // es el título y repetirlo debajo sobra.
    message: texto(origen, 'title') ? origen.getAttribute(ATTR) : texto(origen, 'message'),
    detail: texto(origen, 'detail'),
    confirmText: texto(origen, 'ok'),
    cancelText: texto(origen, 'cancel'),
    danger: origen.hasAttribute(`${ATTR}-danger`),
  });
}

/**
 * Deja que la acción siga su curso, ya confirmada.
 *
 * `requestSubmit` y no `submit`: el segundo se salta la validación del
 * navegador y el evento `submit`, así que un formulario con campos obligatorios
 * a medio llenar se enviaría igual. Con la marca puesta, el `submit` que
 * dispara vuelve a pasar por aquí y se deja pasar.
 */
function seguir(origen, submitter) {
  origen[YA] = true;

  if (origen.tagName === 'FORM') {
    if (typeof origen.requestSubmit === 'function') origen.requestSubmit(submitter || undefined);
    else origen.submit();

    return;
  }

  origen.click();
}

/**
 * Preguntar y, si se dice que sí, dejar seguir.
 *
 * **Si la acción sale de un `<dialog>` abierto, se aparta solo**: ese vive en
 * la capa de encima del navegador y el modal saldría debajo. De eso se encarga
 * `dialog.js`, que lo cierra para preguntar y lo devuelve como estaba - así
 * cancelar deja la pantalla igual que antes de pulsar -.
 */
function preguntarYSeguir(origen, submitter) {
  preguntar(origen).then((si) => {
    if (si) seguir(origen, submitter);
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!form || typeof form.hasAttribute !== 'function' || !form.hasAttribute(ATTR)) return;

      if (form[YA]) {
        form[YA] = false;
        return;
      }

      event.preventDefault();

      // `submitter` se pierde en cuanto el evento termina: hay formularios con
      // dos botones -«Guardar» y «Guardar y seguir»- y el servidor distingue por
      // el nombre del que se pulsó.
      preguntarYSeguir(form, event.submitter);
    },
    // En captura: así se llega antes que cualquier `onsubmit` de la página, que
    // es lo que se quiere - preguntar primero, actuar después -.
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const origen = event.target.closest && event.target.closest(`a[${ATTR}], button[${ATTR}]`);

      if (!origen) return;

      if (origen[YA]) {
        origen[YA] = false;
        return;
      }

      // Un botón dentro de un formulario que ya lo declara: preguntar dos veces
      // sería preguntar dos veces. Manda el del formulario.
      if (origen.form && origen.form.hasAttribute(ATTR)) return;

      event.preventDefault();

      preguntarYSeguir(origen);
    },
    true
  );
}

export { ATTR as CONFIRM_ATTR };
